import { RoleEnum } from "@ct/innermatrix/src/graphql/generatedTypes/globalTypes";
import { CTRoleEnum } from "@ct/conscioustransformation/src/graphql/generatedTypes/globalTypes";

const JWT_KEY = "jwt";
const CURRENT_USER_KEY = "currentUser";
const IS_VIEWING_CMS_CONTENT_KEY = "isViewingCmsContent";
const HAS_MEMBERSHIP = "hasMembership";
const TRAINER_ID = "trainerId";

const roleLevels = new Map<RoleEnum, number>();
roleLevels.set(RoleEnum.PARTICIPANT, 0);
roleLevels.set(RoleEnum.TRAINER, 3);
roleLevels.set(RoleEnum.ADMIN, 4);

export interface Auth {
  jwt: string | null;
  currentUser: CurrentUserJSON | null;
  hasMembership: boolean;
  trainerId?: string | null;
}

interface CurrentUserJSON {
  id: string;
  email: string;
  imgRole: string;
  phone: string;
  firstName: string;
  lastName: string;
  ctRole: string;
  hasActiveMembership: boolean;
  hasFailedMembership: boolean;
  isEligibleForFirstTimeDiscount?: boolean | undefined;
  isEligibleForReengagementPricing?: boolean | undefined;
}

export class CurrentUser {
  id: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  imgRole: RoleEnum;
  ctRole: CTRoleEnum;
  hasActiveMembership: boolean;
  hasFailedMembership: boolean;
  isEligibleForFirstTimeDiscount?: boolean | null;
  isEligibleForReengagementPricing?: boolean | null;

  constructor(currentUserJSON: CurrentUserJSON) {
    this.id = currentUserJSON.id;
    this.email = currentUserJSON.email;
    this.firstName = currentUserJSON.firstName;
    this.lastName = currentUserJSON.lastName;
    this.phone = currentUserJSON.phone;
    this.imgRole = this.convertStringToRoleEnum(currentUserJSON.imgRole);
    this.ctRole = this.convertStringToCTRoleEnum(currentUserJSON.ctRole);
    this.hasActiveMembership = currentUserJSON.hasActiveMembership;
    this.hasFailedMembership = currentUserJSON.hasFailedMembership;
    this.isEligibleForFirstTimeDiscount = currentUserJSON.isEligibleForFirstTimeDiscount;
    this.isEligibleForReengagementPricing = currentUserJSON.isEligibleForReengagementPricing;
  }

  canEdit(role: string): boolean {
    if (this.imgRole === RoleEnum.ADMIN) {
      return true;
    } else if (this.imgRole !== RoleEnum.TRAINER) {
      return false;
    }

    let roleEnum = this.convertStringToRoleEnum(role);

    return roleEnum === RoleEnum.PARTICIPANT;
  }

  canEditCTRole(): boolean {
    return this.isCtAdminOrSuper();
  }

  isCtAdminOrSuper(): boolean {
    return this.ctRole === CTRoleEnum.CT_ADMIN || this.ctRole === CTRoleEnum.CT_SUPER_ADMIN;
  }

  isCtTrainer(): boolean {
    return this.ctRole === CTRoleEnum.CT_TRAINER;
  }

  isCtTrainerOrAdmin(): boolean {
    return this.isCtTrainer() || this.isCtAdminOrSuper();
  }

  hasPermissionLevelOrGreater(role: RoleEnum): boolean {
    let currentUserRoleValue = roleLevels.get(this.imgRole);
    let comparisonRoleValue = roleLevels.get(role);

    if (currentUserRoleValue === undefined || comparisonRoleValue === undefined) {
      return false;
    } else {
      return currentUserRoleValue >= comparisonRoleValue;
    }
  }

  private convertStringToRoleEnum(role: string): RoleEnum {
    switch (role.toUpperCase()) {
      case RoleEnum.TRAINER:
        return RoleEnum.TRAINER;
      case RoleEnum.ADMIN:
        return RoleEnum.ADMIN;
      default:
        return RoleEnum.PARTICIPANT;
    }
  }

  private convertStringToCTRoleEnum(role: string): CTRoleEnum {
    switch (role.toUpperCase()) {
      case CTRoleEnum.CT_SUPER_ADMIN:
        return CTRoleEnum.CT_SUPER_ADMIN;
      case CTRoleEnum.CT_ADMIN:
        return CTRoleEnum.CT_ADMIN;
      case CTRoleEnum.CT_TRAINER:
        return CTRoleEnum.CT_TRAINER;
      case CTRoleEnum.CT_LEAD_TRAINER:
        return CTRoleEnum.CT_LEAD_TRAINER;
      case CTRoleEnum.CT_APPRENTICE_TRAINER:
        return CTRoleEnum.CT_APPRENTICE_TRAINER;
      default:
        return CTRoleEnum.CT_USER;
    }
  }
}

export class AuthRepository {
  static setFirstTimeDiscountEligibility(value: boolean): void {
    const currentUserJSON = localStorage.getItem(CURRENT_USER_KEY);

    if (!currentUserJSON) return;

    const newCurrentUser = new CurrentUser({
      ...JSON.parse(currentUserJSON),
      isEligibleForFirstTimeDiscount: value
    });

    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(newCurrentUser));
  }

  static setReengagementDiscountEligibility(value: boolean): void {
    const currentUserJSON = localStorage.getItem(CURRENT_USER_KEY);

    if (!currentUserJSON) return;

    const newCurrentUser = new CurrentUser({
      ...JSON.parse(currentUserJSON),
      isEligibleForReengagementPricing: value
    });

    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(newCurrentUser));
  }

  static getJWT(): string | null {
    return localStorage.getItem(JWT_KEY);
  }

  static getCurrentUser(): CurrentUser | null {
    const currentUserJSON = localStorage.getItem(CURRENT_USER_KEY);
    if (!currentUserJSON) {
      return null;
    }

    const valid = AuthRepository.validateCurrentUser(JSON.parse(currentUserJSON));
    if (!valid) {
      this.signOut();
      document.location.href = "/";
      console.error("Invalid user returned, please login with valid user");
      return null;
    }

    return new CurrentUser(JSON.parse(currentUserJSON));
  }

  static setShopAsUser(shopAsUser: { id: string; firstName: string; lastName: string }) {
    localStorage.setItem("shopAsUser", JSON.stringify(shopAsUser));
  }

  static getShopAsUser(): {
    userId: string;
    fullName: string;
    hasMembership: boolean;
    membershipTrainer: string;
    isEligibleForFirstTimeDiscount: boolean;
    isEligibleForReengagementPricing: boolean;
  } | null {
    const shopAsUser = localStorage.getItem("shopAsUser");
    if (!shopAsUser) {
      return null;
    }
    const parsedShopAsUser = JSON.parse(shopAsUser);
    return {
      userId: parsedShopAsUser.id,
      fullName: `${parsedShopAsUser.firstName} ${parsedShopAsUser.lastName}`,
      hasMembership: parsedShopAsUser.hasActiveMembership,
      membershipTrainer: parsedShopAsUser.selectedMembershipTrainer,
      isEligibleForFirstTimeDiscount: parsedShopAsUser.isEligibleForFirstTimeDiscount,
      isEligibleForReengagementPricing: parsedShopAsUser.isEligibleForReengagementPricing
    };
  }

  static isCtAdminOrSuper(): boolean {
    return this.getCurrentUser()?.isCtAdminOrSuper() || false;
  }

  static isCtTrainer(): boolean {
    return this.getCurrentUser()?.isCtTrainer() || false;
  }

  static isCtTrainerOrAdmin(): boolean {
    return this.getCurrentUser()?.isCtTrainerOrAdmin() || false;
  }

  static clearShopAsUser() {
    localStorage.removeItem("shopAsUser");
  }

  static canEditCmsContent() {
    return AuthRepository.getCurrentUser()?.ctRole === CTRoleEnum.CT_SUPER_ADMIN;
  }

  static isViewingCmsContent() {
    return localStorage.getItem(IS_VIEWING_CMS_CONTENT_KEY) === "true" && this.canEditCmsContent();
  }

  static enableViewingCmsContent() {
    localStorage.setItem(IS_VIEWING_CMS_CONTENT_KEY, "true");
  }

  static disableViewingCmsContent() {
    localStorage.removeItem(IS_VIEWING_CMS_CONTENT_KEY);
  }

  static hasMembership(): boolean {
    const user = this.getCurrentUser();

    if (!user) return false;

    return String(user.hasActiveMembership) === "true";
  }

  static hasFailedMembership(): boolean {
    const user = this.getCurrentUser();

    if (!user) return false;

    return String(user.hasFailedMembership) === "true";
  }

  static setHasMembership(newStatus: boolean): void {
    const currentUserJSON = localStorage.getItem(CURRENT_USER_KEY);
    if (!currentUserJSON) return;
    const newCurrentUser = new CurrentUser({
      ...JSON.parse(currentUserJSON),
      hasActiveMembership: newStatus.toString()
    });

    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(newCurrentUser));
    localStorage.setItem(HAS_MEMBERSHIP, newStatus.toString());
  }

  static getMembershipTrainerId(): string | null {
    return localStorage.getItem(TRAINER_ID);
  }

  static setMembershipTrainerId(trainerId: string): void {
    localStorage.setItem(TRAINER_ID, trainerId.toString());
  }

  static effectiveHasMembership() {
    return this.getShopAsUser() ? this.getShopAsUser()?.hasMembership : this.hasMembership();
  }

  static effectiveMembershipTrainerId() {
    return this.getShopAsUser() ? this.getShopAsUser()?.membershipTrainer : this.getMembershipTrainerId();
  }

  static setUserNeedsToUpdateProfile(firstName: string, lastName: string) {
    if (firstName === "first" || lastName === "last") {
      localStorage.setItem("updateProfile", "true");
    } else {
      localStorage.removeItem("updateProfile");
    }
  }

  static getUserNeedsToUpdateProfile() {
    return localStorage.getItem("updateProfile") === "true";
  }

  private static validateCurrentUser(user: any): boolean {
    const sampleCurrentUserJSON: CurrentUserJSON = {
      id: "",
      imgRole: "role",
      ctRole: "ct_role",
      email: "email@test.com",
      phone: "939393939393",
      firstName: "hello",
      lastName: "world",
      hasActiveMembership: false,
      hasFailedMembership: false
    };

    const sampleCurrentUserJSONKeys: string[] = Object.keys(sampleCurrentUserJSON);
    const missingKeys = sampleCurrentUserJSONKeys.filter((userKey: string) => {
      return user[userKey] === undefined;
    });
    return missingKeys.length === 0;
  }

  static signIn(auth: Auth) {
    if (!auth.jwt) {
      return;
    }
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(auth.currentUser));
    localStorage.setItem(JWT_KEY, auth.jwt);
    localStorage.setItem(HAS_MEMBERSHIP, auth.hasMembership.toString());
    if (auth.trainerId) {
      localStorage.setItem(TRAINER_ID, auth.trainerId);
    }
  }

  static signOut() {
    localStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem(JWT_KEY);
    localStorage.removeItem(HAS_MEMBERSHIP);
    localStorage.removeItem(TRAINER_ID);

    this.clearShopAsUser();
  }
}
